// Shared resolver for curated product picks. Hydrates a CatalogPick (catalog key
// + reason) into a full ProductRecommendationLink, reading names, ASINs, affiliate
// URLs, prices, and images from the single catalog source
// (data/guide-product-recommendations.ts). Tools own only their (result -> keys)
// mapping; product fields flow through automatically. Extracted from the soil-test
// engine (lib/soil-test/product-picks.ts) so every calculator shares one resolver.

import { getProductByKey, type ProductKey } from '@/data/guide-product-recommendations'
import type { CatalogPick, ProductRecommendationLink } from './types'

/** Extract a 10-char ASIN from an Amazon URL when present, else null. */
export function extractAsin(url: string | null | undefined): string | null {
  if (!url) return null
  const patterns = [
    /\/dp\/([A-Z0-9]{10})(?:[/?]|$)/i,
    /\/gp\/product\/([A-Z0-9]{10})(?:[/?]|$)/i,
    /[?&]asin=([A-Z0-9]{10})(?:&|$)/i,
  ]
  for (const p of patterns) {
    const m = url.match(p)
    if (m) return m[1].toUpperCase()
  }
  return null
}

/** Hydrate one catalog key + reason into a product link. Null if the key is missing. */
export function linkFromCatalog(key: ProductKey, reason: string): ProductRecommendationLink | null {
  const product = getProductByKey(key)
  if (!product) return null
  return {
    asin: extractAsin(product.affiliateUrl),
    name: product.name,
    affiliateUrl: product.affiliateUrl,
    reason,
    imageUrl: product.imageUrl,
    price: product.price,
    category: product.category,
  }
}

/** Resolve an ordered list of picks, dropping any whose catalog key is missing. */
export function resolvePicks(picks: CatalogPick[]): ProductRecommendationLink[] {
  const out: ProductRecommendationLink[] = []
  for (const p of picks) {
    const link = linkFromCatalog(p.key, p.reason)
    if (link) out.push(link)
  }
  return out
}

/** Dedupe a flat list of links by ASIN (else name), preserving first appearance. */
export function dedupeLinks(links: ProductRecommendationLink[]): ProductRecommendationLink[] {
  const seen = new Set<string>()
  const out: ProductRecommendationLink[] = []
  for (const link of links) {
    const key = link.asin ?? link.name
    if (seen.has(key)) continue
    seen.add(key)
    out.push(link)
  }
  return out
}

/**
 * Sum the catalog price strings (e.g. "$60", "$8") of a product list.
 * Returns null if no products carry a price (so the UI can hide the total).
 */
export function sumPrices(products: ProductRecommendationLink[]): number | null {
  let total = 0
  let counted = 0
  for (const p of products) {
    if (!p.price) continue
    const n = Number(p.price.replace(/[^\d.]/g, ''))
    if (Number.isFinite(n)) {
      total += n
      counted += 1
    }
  }
  return counted > 0 ? total : null
}
